"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { SwipeTabItem } from "./useSwipeTabs";

export type SwipeTabDirection = "left" | "right";
export type SwipeTabPanelTransitionMode = "route" | "component";

/** 默认面板入场动画时长，需要与全局 CSS 中的动画时长保持一致。 */
const DEFAULT_SWIPE_TAB_PANEL_TRANSITION_MS = 700;
/** 通用面板入场动画 class，key 表示用户手势方向。 */
export const SWIPE_TAB_PANEL_TRANSITION_CLASS: Record<
  SwipeTabDirection,
  string
> = {
  left: "swipe-tab-panel-enter-left",
  right: "swipe-tab-panel-enter-right",
};

type UseSwipeTabPanelTransitionParams<T extends string> = {
  /** 按显示顺序排列的 tab 列表，用于计算新旧 tab 的方向关系 */
  tabs: SwipeTabItem<T>[];
  /** 当前激活 tab id，路由切换或同页切换完成后会触发入场动画读取 */
  activeId: T | undefined;
  /**
   * 转场状态传递模式：
   * - route：适合路由型 tab，当前页面会卸载，需要用 sessionStorage 把方向交给新页面；
   * - component：适合同页组件切换，当前组件不卸载，用内存 ref 等待 activeId 更新后播放动画。
   */
  transitionMode?: SwipeTabPanelTransitionMode;
  /** 页面级唯一 storage key，仅 route 模式需要，用于跨路由保存本次手势方向 */
  storageKey?: string;
  /** 动画开关，通常与 useSwipeTabs 的 enabled 保持一致 */
  enabled?: boolean;
  /** 清理动画 class 的延迟，需要与 CSS 动画时长一致 */
  animationMs?: number;
};

/**
 * 根据“当前 tab -> 目标 tab”的索引关系计算手势方向（只做纯计算，不做副作用）。
 *
 * 约定：
 * - 用户左滑（内容向左拖）通常意味着“去下一个 tab”，新面板应从右侧进入；
 * - 用户右滑通常意味着“回上一个 tab”，新面板应从左侧进入。
 *
 * 返回 undefined 的场景：
 * - activeId 缺失；
 * - nextId 与 activeId 相同；
 * - 任一 id 不在 tabs 列表中（异步数据切换窗口、配置变更等）。
 */
const getNextSwipeTabDirection = <T extends string>({
  tabs,
  activeId,
  nextId,
}: {
  tabs: SwipeTabItem<T>[];
  activeId: T | undefined;
  nextId: T;
}): SwipeTabDirection | undefined => {
  if (!activeId || activeId === nextId) {
    return undefined;
  }

  const currentIndex = tabs.findIndex((tab) => tab.id === activeId);
  const nextIndex = tabs.findIndex((tab) => tab.id === nextId);
  if (currentIndex < 0 || nextIndex < 0 || currentIndex === nextIndex) {
    return undefined;
  }

  return nextIndex > currentIndex ? "left" : "right";
};

/**
 * 手势切 tab 的通用面板入场动画 hook。
 *
 * 职责边界：
 * - 不监听手势，只接收“即将切到哪个 tab”并计算方向；
 * - route 模式通过 sessionStorage 保存方向，兼容路由型 tab 的新页面挂载动画；
 * - component 模式通过内存 ref 暂存方向，兼容同页组件切换后的入场动画；
 * - 返回通用 className，调用方应把它加到纯视觉内层容器，避免影响滚动/高度容器。
 *
 * 关键设计点：
 * 1) 只做“新面板入场”动画，不接管旧面板离场，降低对现有渲染结构的侵入。
 * 2) route/component 两种模式共享同一套方向语义与 class，保证视觉一致。
 * 3) 动画状态在 hook 内自动清理，避免 class 常驻导致后续切换不触发动画。
 *
 * 容器约束（非常重要）：
 * - transitionClassName 只挂纯视觉内层容器；
 * - 不挂滚动容器、最小高度容器、ScrollRestorer/HeightRecorder 相关容器；
 * - 原因：class 内含 transform，可能影响滚动恢复与高度记录链路。
 */
export const useSwipeTabPanelTransition = <T extends string>({
  tabs,
  activeId,
  transitionMode = "route",
  storageKey,
  enabled = true,
  animationMs = DEFAULT_SWIPE_TAB_PANEL_TRANSITION_MS,
}: UseSwipeTabPanelTransitionParams<T>): {
  /** 当前需要挂到内容视觉容器上的通用动画 className */
  transitionClassName: string | undefined;
  /** 当前动画方向，主要用于调试或特殊场景判断 */
  transitionDirection: SwipeTabDirection | undefined;
  /** 在触发 tab 切换前调用，用于准备本次切换的方向信息 */
  prepareTransition: (nextId: T) => SwipeTabDirection | undefined;
  /** 只计算方向，不写 storage */
  getTransitionDirection: (nextId: T) => SwipeTabDirection | undefined;
} => {
  /**
   * 当前“正在播放”的动画方向。
   * 存在即代表应该给视觉容器挂 class；清空后 class 自动移除。
   */
  const [transitionDirection, setTransitionDirection] = useState<
    SwipeTabDirection | undefined
  >(undefined);
  /**
   * component 模式专用缓存：
   * prepareTransition(nextId) 发生在 activeId 更新前，方向先暂存到这里；
   * 待 activeId 真正变化后，再开始动画，确保动画作用在“新内容”上。
   */
  const pendingComponentDirectionRef = useRef<SwipeTabDirection | undefined>(
    undefined,
  );
  /**
   * component 模式中用于识别“是否真的发生了 tab 切换”。
   * 仅当 activeId 变化时才播放，避免同 tab 重复点击或数据刷新误触发动画。
   */
  const previousActiveIdRef = useRef<T | undefined>(activeId);
  /** 当前动画清理定时器句柄，连续切换时先清上一个再开新一个。 */
  const clearTransitionTimerRef = useRef<number | undefined>(undefined);

  const getTransitionDirection = useCallback(
    (nextId: T) =>
      getNextSwipeTabDirection({
        tabs,
        activeId,
        nextId,
      }),
    [activeId, tabs],
  );

  const prepareTransition = useCallback(
    (nextId: T) => {
      // 统一动画开关，禁用时直接 no-op。
      if (!enabled) {
        return undefined;
      }
      const direction = getTransitionDirection(nextId);
      if (!direction) {
        return undefined;
      }

      if (transitionMode === "component") {
        /**
         * 同页组件切换模式：
         * - 不需要 sessionStorage；
         * - 只缓存方向，等待 activeId 变化后播放；
         * - 连续同方向切换时，先清掉当前 class，保证 CSS animation 可重新触发。
         */
        pendingComponentDirectionRef.current = direction;
        setTransitionDirection(undefined);
        if (clearTransitionTimerRef.current) {
          window.clearTimeout(clearTransitionTimerRef.current);
          clearTransitionTimerRef.current = undefined;
        }
        return direction;
      }

      try {
        if (typeof window !== "undefined" && storageKey) {
          /**
           * 路由切换模式：
           * 当前页面会被卸载，无法在本页等待 activeId 变化再播动画，
           * 所以先写入 sessionStorage，由新页面挂载后一次性消费。
           */
          window.sessionStorage.setItem(storageKey, direction);
        }
      } catch {
        // storage 不可用时只降级为无动画，不影响 tab 切换主流程。
      }

      return direction;
    },
    [enabled, getTransitionDirection, storageKey, transitionMode],
  );

  const startTransition = useCallback(
    (direction: SwipeTabDirection) => {
      if (clearTransitionTimerRef.current) {
        window.clearTimeout(clearTransitionTimerRef.current);
      }

      // 挂载 class，启动入场动画。
      setTransitionDirection(direction);
      // 到时自动清理 class，避免常驻影响下一次动画触发。
      clearTransitionTimerRef.current = window.setTimeout(() => {
        setTransitionDirection(undefined);
        clearTransitionTimerRef.current = undefined;
      }, animationMs);
    },
    [animationMs],
  );

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    if (transitionMode === "component") {
      // 同页模式：以 activeId 变化作为“新内容已生效”的时机。
      const previousActiveId = previousActiveIdRef.current;
      previousActiveIdRef.current = activeId;
      if (previousActiveId === activeId) {
        return;
      }

      const pendingDirection = pendingComponentDirectionRef.current;
      if (!pendingDirection) {
        return;
      }

      pendingComponentDirectionRef.current = undefined;
      startTransition(pendingDirection);
      return;
    }

    if (!storageKey) {
      // route 模式下没传 key 时无法跨页传递方向，直接降级为无动画。
      return;
    }

    let storedDirection: string | null;
    try {
      storedDirection = window.sessionStorage.getItem(storageKey);
    } catch {
      return;
    }
    if (storedDirection !== "left" && storedDirection !== "right") {
      return;
    }

    // 一次性消费，防止刷新或下一次非手势切换误读旧方向。
    window.sessionStorage.removeItem(storageKey);
    startTransition(storedDirection);
  }, [activeId, enabled, startTransition, storageKey, transitionMode]);

  useEffect(() => {
    // 组件卸载时兜底清理，避免残留 timer。
    return () => {
      if (clearTransitionTimerRef.current) {
        window.clearTimeout(clearTransitionTimerRef.current);
      }
    };
  }, []);

  return {
    // 方向到 class 的唯一映射，调用方无需关心映射细节。
    transitionClassName: transitionDirection
      ? SWIPE_TAB_PANEL_TRANSITION_CLASS[transitionDirection]
      : undefined,
    transitionDirection,
    prepareTransition,
    getTransitionDirection,
  };
};
